import { useEffect, useRef, useState } from 'react';
import { vexStockImages } from '~/constants/environment';
import optimizeImage from '~/utilities/optimizeImage';
import {
  computeEllipseLeft,
  DESKTOP_ELLIPSE_MASK_MD,
  DESKTOP_ELLIPSE_MASK_XL,
  HERO_HEIGHT,
  MOBILE_MASK,
  TABLET_MASK
} from './OrderStatusModule.masks';
import {
  DesktopEllipse,
  DesktopEllipseImg,
  TabletImg
} from './OrderStatusModule.styles';
import styles from './VehicleHeroImage.module.css';

type OwnProps = {
  children: React.ReactNode;
  imageUrl: string;
};

export default function VehicleHeroImage({ children, imageUrl }: OwnProps) {
  const desktopHeroRef = useRef<HTMLDivElement>(null);
  const [desktopEllipseLeft, setDesktopEllipseLeft] = useState(
    computeEllipseLeft(1024)
  );

  const genericImageUrl = optimizeImage(
    `${vexStockImages}/generic_vehicle_desktop_1920x1080.png`,
    { crop: '21:9', height: 600 }
  );

  const desktopImageSrc = optimizeImage(imageUrl, {
    format: 'auto',
    height: 600,
    trim: '0,0,80'
  });
  const imageSrc = optimizeImage(imageUrl, { format: 'auto' });

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (e.currentTarget.src !== genericImageUrl) {
      e.currentTarget.src = genericImageUrl;
    }
  };

  useEffect(() => {
    const el = desktopHeroRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() =>
      setDesktopEllipseLeft(computeEllipseLeft(el.clientWidth))
    );
    ro.observe(el);
    setDesktopEllipseLeft(computeEllipseLeft(el.clientWidth));

    return () => ro.disconnect();
  }, []);

  return (
    <>
      {/* ── Mobile (<768): full-bleed image with curved arc, card below ── */}
      <div className="bg-background-strong sm:hidden">
        <img
          alt=""
          aria-hidden="true"
          className="block h-[210px] w-full select-none object-cover"
          onError={handleImageError}
          src={imageSrc}
          style={MOBILE_MASK}
        />
        <div className="mx-auto w-full max-w-[1352px] p-2xl">{children}</div>
      </div>

      {/* ── Tablet (768–1023): ellipse reveal, card overlays left zone ── */}
      <div
        className="relative hidden w-full overflow-hidden bg-background-strong sm:block md:hidden"
        style={{ minHeight: HERO_HEIGHT }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 select-none"
          style={TABLET_MASK}
        >
          <TabletImg
            alt=""
            aria-hidden="true"
            onError={handleImageError}
            src={imageSrc}
          />
        </div>
        <div
          className="relative flex w-full flex-col justify-center py-40"
          style={{ minHeight: HERO_HEIGHT }}
        >
          <div className="mx-auto w-full max-w-[1352px] px-3xl">
            <div className="max-w-[420px]">{children}</div>
          </div>
        </div>
      </div>

      {/* ── Desktop (≥1024): card on left, ellipse image on right ── */}
      <div
        ref={desktopHeroRef}
        className="relative hidden w-full overflow-hidden bg-background-strong md:block"
        style={{ height: HERO_HEIGHT }}
      >
        <div className="relative mx-auto h-full w-full max-w-[1352px]">
          <div className="relative z-[2] flex h-full max-w-[735px] flex-col justify-center pl-4xl">
            {children}
          </div>
          <div
            aria-hidden="true"
            className="absolute inset-y-0 left-full z-[1] w-0"
          >
            <DesktopEllipse
              className={styles.ellipse}
              style={
                {
                  '--ellipse-mask-md': DESKTOP_ELLIPSE_MASK_MD,
                  '--ellipse-mask-xl': DESKTOP_ELLIPSE_MASK_XL,
                  left: desktopEllipseLeft
                } as React.CSSProperties
              }
            >
              <DesktopEllipseImg
                onError={handleImageError}
                src={desktopImageSrc}
                style={{ height: HERO_HEIGHT }}
              />
            </DesktopEllipse>
          </div>
        </div>
      </div>
    </>
  );
}
